
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Agent } from "@/models/agent";
import { User } from "@/models/user";
import { AgentPriceTag } from "./purchase/AgentPriceTag";
import { AgentPurchaseButton } from "./purchase/AgentPurchaseButton";
import { AgentSocialActions } from "./purchase/AgentSocialActions";
import { AgentMetadata } from "./purchase/AgentMetadata";
import { WalletBalance } from "./purchase/WalletBalance";
import { useAgentPurchase } from "@/hooks/use-agent-purchase";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

interface AgentPurchaseCardProps {
  agent: Agent;
  user: User | null;
  walletBalance: number | null;
  setWalletBalance: (balance: number) => void;
  purchaseSuccess: boolean;
  setPurchaseSuccess: (success: boolean) => void;
}

export const AgentPurchaseCard: React.FC<AgentPurchaseCardProps> = ({ 
  agent, 
  user, 
  walletBalance, 
  setWalletBalance, 
  purchaseSuccess, 
  setPurchaseSuccess 
}) => {
  const {
    purchasing,
    isFavorite,
    isCheckingStatus,
    error,
    handlePurchase,
    handleFavorite,
    handleShare
  } = useAgentPurchase({
    agent,
    user,
    walletBalance,
    setWalletBalance,
    purchaseSuccess,
    setPurchaseSuccess
  });

  return (
    <div className="flex flex-col gap-4 min-w-[220px]">
      <Card>
        <CardContent className="p-4">
          <div className="text-center mb-4">
            <AgentPriceTag pricingModel={agent.pricingModel} price={agent.price} />
            
            {isCheckingStatus ? (
              <Skeleton className="h-10 w-full mb-3 rounded" />
            ) : (
              <AgentPurchaseButton
                purchasing={purchasing}
                purchaseSuccess={purchaseSuccess}
                pricingModel={agent.pricingModel}
                handlePurchase={handlePurchase}
              />
            )}
            
            {error && (
              <Alert variant="destructive" className="mt-2 text-sm">
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <WalletBalance 
              user={user} 
              walletBalance={walletBalance}
              pricingModel={agent.pricingModel}
            />
          </div>
          
          <AgentSocialActions
            isFavorite={isFavorite}
            handleFavorite={handleFavorite}
            handleShare={handleShare}
          />
        </CardContent>
      </Card>
      
      <AgentMetadata agent={agent} />
    </div>
  );
};
